import { useEffect, useState, useCallback } from "react";
import { Star, Trash2, Check, EyeOff, Plus, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const SERVICES = [
  "Excavation", "Concrete & Foundations", "Site Prep & Grading",
  "Decorative & Stamped Concrete", "Retaining Walls",
  "Driveways, Paving & Surfacing", "Drainage", "Other",
];

const inputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2.5 text-sm text-[#23282B]";

export function ReviewsTab({ onCountChange }) {
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState(null);
  const [modal, setModal] = useState(false);

  const refresh = useCallback(() => {
    api.get("/testimonials/all").then((r) => {
      const data = r.data || [];
      setRows(data);
      onCountChange?.(data.filter((t) => !t.approved).length);
    }).catch(() => {});
  }, [onCountChange]);
  useEffect(() => { refresh(); }, [refresh]);

  const patch = async (id, updates, okText) => {
    setMsg(null);
    try { await api.patch(`/testimonials/${id}`, updates); setMsg({ kind: "ok", text: okText }); refresh(); }
    catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the review from ${t.name}?`)) return;
    try { await api.delete(`/testimonials/${t.id}`); setMsg({ kind: "ok", text: "Review deleted." }); refresh(); }
    catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
  };

  const pending = rows.filter((t) => !t.approved);
  const approved = rows.filter((t) => t.approved);

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-5 md:p-8" data-testid="reviews-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Customer Reviews</div>
          <p className="mt-1 text-sm text-[#23282B]/60">Reviews submitted on the public site wait here for approval before they appear on the homepage.</p>
        </div>
        <button onClick={() => setModal(true)} data-testid="reviews-add-btn" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
          <Plus size={15} /> Add Review
        </button>
      </div>

      {msg && (
        <div data-testid={`reviews-msg-${msg.kind}`} className={`mt-4 px-4 py-3 text-sm border-l-4 ${msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-6" data-testid="reviews-pending">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-amber-700">Awaiting Approval ({pending.length})</div>
          <div className="mt-3 space-y-3">
            {pending.map((t) => <ReviewRow key={t.id} t={t} onPatch={patch} onDelete={remove} />)}
          </div>
        </div>
      )}

      <div className="mt-6" data-testid="reviews-approved">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#2F5F80]">Live on the Site ({approved.length})</div>
        {approved.length === 0 ? (
          <p className="mt-3 text-sm text-[#23282B]/50">No approved reviews yet.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {approved.map((t) => <ReviewRow key={t.id} t={t} onPatch={patch} onDelete={remove} />)}
          </div>
        )}
      </div>

      {modal && <AddReviewModal onClose={() => setModal(false)} onSaved={() => { refresh(); setMsg({ kind: "ok", text: "Review added and live." }); }} />}
    </section>
  );
}

function ReviewRow({ t, onPatch, onDelete }) {
  return (
    <article className={`border-2 p-4 ${t.approved ? "border-[#23282B]/10" : "border-amber-400 bg-amber-50/40"}`} data-testid={`review-row-${t.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg uppercase text-[#23282B]">{t.name}</span>
            <span className="flex gap-0.5">{[1,2,3,4,5].map((i) => <Star key={i} size={12} className={i <= t.rating ? "text-[#2F5F80] fill-[#2F5F80]" : "text-[#23282B]/20"} />)}</span>
            {t.sample && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#9CC4DB]/30 text-[#2F5F80] px-2 py-0.5">Sample — replace with real reviews</span>}
          </div>
          <div className="text-xs text-[#23282B]/60 mt-0.5">{t.location}{t.location ? " · " : ""}{t.service} · {t.created_at?.slice(0, 10)}</div>
          <p className="mt-2 text-sm text-[#23282B]/85">“{t.quote}”</p>
        </div>
        <div className="flex items-center gap-2">
          {t.approved ? (
            <button onClick={() => onPatch(t.id, { approved: false }, "Review hidden from the site.")} data-testid={`review-hide-${t.id}`} className="inline-flex items-center gap-1.5 border-2 border-[#23282B]/20 hover:border-[#23282B] text-[#23282B] text-xs font-bold uppercase tracking-[0.1em] px-3 py-2.5 transition-colors">
              <EyeOff size={13} /> Hide
            </button>
          ) : (
            <button onClick={() => onPatch(t.id, { approved: true }, "Review approved — now live on the homepage.")} data-testid={`review-approve-${t.id}`} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase tracking-[0.1em] px-3 py-2.5 transition-colors">
              <Check size={13} /> Approve
            </button>
          )}
          <button onClick={() => onDelete(t)} data-testid={`review-delete-${t.id}`} className="p-2 text-red-600 hover:text-red-800" title="Delete review">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </article>
  );
}

function AddReviewModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", location: "", service: "Other", rating: 5, quote: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await api.post("/testimonials/admin", form); onSaved(); onClose(); }
    catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="reviews-add-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg p-7 border-t-4 border-[#2F5F80] my-auto max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl uppercase text-[#23282B]">Add Review</div>
          <button type="button" onClick={onClose} className="p-1 text-[#23282B]/60 hover:text-[#23282B]"><X size={20} /></button>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Customer Name *</span>
            <input data-testid="reviews-form-name" required value={form.name} onChange={set("name")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Location</span>
            <input data-testid="reviews-form-location" value={form.location} onChange={set("location")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Service</span>
            <select data-testid="reviews-form-service" value={form.service} onChange={set("service")} className={inputCls}>{SERVICES.map((s) => <option key={s}>{s}</option>)}</select></label>
          <div>
            <span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Rating</span>
            <div className="flex gap-0.5 pt-1">
              {[1,2,3,4,5].map((i) => (
                <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} className="p-1 active:scale-95">
                  <Star size={22} className={i <= form.rating ? "text-[#2F5F80] fill-[#2F5F80]" : "text-[#23282B]/25"} />
                </button>
              ))}
            </div>
          </div>
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Review * (min 10 chars)</span>
            <textarea data-testid="reviews-form-quote" required minLength={10} rows={4} value={form.quote} onChange={set("quote")} className={inputCls} /></label>
        </div>
        {error && <div className="mt-4 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-700">{error}</div>}
        <button type="submit" disabled={busy} data-testid="reviews-form-submit" className="mt-6 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors disabled:opacity-50">
          {busy ? "Saving…" : "Add & Publish"}
        </button>
      </form>
    </div>
  );
}
